import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { API, uploadImage, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Plus, ExternalLink, Trash2, Users, ImagePlus, X, Copy } from "lucide-react";

export default function LandingPagesView() {
  const [pages, setPages] = useState(null);
  const [listings, setListings] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [leadsFor, setLeadsFor] = useState(null);
  const [leads, setLeads] = useState([]);
  const fileRef = useRef(null);
  const headshotRef = useRef(null);
  const [form, setForm] = useState({
    listing_id: "", title: "", headline: "", description: "", price: "", address: "",
    photos: [], agent: { name: "", phone: "", email: "", brand_color: "#0F766E", headshot_path: "" },
  });

  const load = async () => {
    try { const { data } = await api.get("/landing"); setPages(data); }
    catch { setPages([]); }
  };
  useEffect(() => { load(); api.get("/listings").then(({ data }) => setListings(data)).catch(() => {}); }, []);

  const pickListing = (id) => {
    const l = listings.find((x) => x.id === id);
    setForm((f) => ({
      ...f, listing_id: id,
      title: l?.title || f.title,
      description: l?.outputs?.description || f.description,
      address: l?.details?.location || f.address,
    }));
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { path } = await uploadImage(file);
        setForm((f) => ({ ...f, photos: [...f.photos, path] }));
      }
      toast.success("Photo(s) uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); }
  };

  const onHeadshot = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const { path } = await uploadImage(file); setForm((f) => ({ ...f, agent: { ...f.agent, headshot_path: path } })); toast.success("Headshot uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const create = async () => {
    if (!form.title && !form.address) { toast.error("Add a title or address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/landing", form);
      toast.success("Landing page published!");
      setCreating(false);
      setForm({ listing_id: "", title: "", headline: "", description: "", price: "", address: "", photos: [], agent: { name: "", phone: "", email: "", brand_color: "#0F766E", headshot_path: "" } });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/landing/${id}`); setPages((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); };
  const viewLeads = async (page) => { setLeadsFor(page); const { data } = await api.get(`/landing/${page.id}/leads`); setLeads(data); load(); };
  const publicUrl = (slug) => `${window.location.origin}/p/${slug}`;
  const copyUrl = (slug) => { navigator.clipboard.writeText(publicUrl(slug)); toast.success("Link copied"); };

  if (pages === null) return <div className="h-24 bg-white border border-slate-200 rounded-lg animate-pulse" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-lg">Property Landing Pages</h2>
          <p className="text-sm text-slate-500">Spin up a single-property mini-site with a lead-capture form.</p>
        </div>
        <Button onClick={() => setCreating(!creating)} data-testid="new-landing-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
          {creating ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}{creating ? "Cancel" : "New page"}
        </Button>
      </div>

      {creating && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="landing-form">
          {listings.length > 0 && (
            <div>
              <Label>Prefill from a saved listing (optional)</Label>
              <Select value={form.listing_id} onValueChange={pickListing}>
                <SelectTrigger className="mt-1.5" data-testid="landing-listing-select"><SelectValue placeholder="Choose a listing…" /></SelectTrigger>
                <SelectContent>{listings.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Title</Label><Input data-testid="landing-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="123 Maple Ave" /></div>
            <div><Label>Price</Label><Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" placeholder="$850,000" /></div>
          </div>
          <div><Label>Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="mt-1.5" placeholder="Modern luxury in Highland Park" /></div>
          <div><Label>Address / area</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" placeholder="Highland Park, Austin TX" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" /></div>
          <div>
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {form.photos.map((p, i) => <img key={i} src={`${API}/files/${p}`} alt="" className="h-20 w-20 object-cover rounded-md border border-slate-200" />)}
              <button onClick={() => fileRef.current?.click()} data-testid="landing-photo-btn" className="h-20 w-20 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-slate-400">
                {uploading ? "…" : <ImagePlus className="h-5 w-5" />}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            <div><Label>Agent name</Label><Input value={form.agent.name} onChange={(e) => setForm({ ...form, agent: { ...form.agent, name: e.target.value } })} className="mt-1.5" /></div>
            <div><Label>Agent phone</Label><Input value={form.agent.phone} onChange={(e) => setForm({ ...form, agent: { ...form.agent, phone: e.target.value } })} className="mt-1.5" /></div>
            <div><Label>Agent email</Label><Input value={form.agent.email} onChange={(e) => setForm({ ...form, agent: { ...form.agent, email: e.target.value } })} className="mt-1.5" /></div>
            <div>
              <Label>Brand color</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={form.agent.brand_color} onChange={(e) => setForm({ ...form, agent: { ...form.agent, brand_color: e.target.value } })} className="h-10 w-14 rounded border border-slate-200" />
                <Button variant="outline" size="sm" onClick={() => headshotRef.current?.click()}>{form.agent.headshot_path ? "Headshot ✓" : "Upload headshot"}</Button>
                <input ref={headshotRef} type="file" accept="image/*" className="hidden" onChange={onHeadshot} />
              </div>
            </div>
          </div>
          <Button onClick={create} disabled={busy} data-testid="landing-publish-btn" className="bg-slate-900 hover:bg-slate-800 text-white h-11 font-semibold">{busy ? "Publishing…" : "Publish landing page"}</Button>
        </div>
      )}

      {pages.length === 0 && !creating && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
          <Globe className="h-10 w-10 text-slate-300 mx-auto" /><p className="text-slate-500 mt-4">No landing pages yet.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="landing-list">
        {pages.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`landing-${p.id}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{p.title || p.address}</p>
              <a href={publicUrl(p.slug)} target="_blank" rel="noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1">{`/p/${p.slug}`} <ExternalLink className="h-3 w-3" /></a>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => copyUrl(p.slug)} className="p-2 text-slate-400 hover:text-slate-900" title="Copy link"><Copy className="h-4 w-4" /></button>
              <button onClick={() => viewLeads(p)} data-testid={`leads-${p.id}`} className="px-2.5 py-1.5 text-sm rounded-md bg-slate-100 hover:bg-slate-200 flex items-center gap-1.5"><Users className="h-4 w-4" /> {p.leads_count}</button>
              <button onClick={() => del(p.id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {leadsFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setLeadsFor(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="leads-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold">Leads — {leadsFor.title}</h3><button onClick={() => setLeadsFor(null)}><X className="h-5 w-5" /></button></div>
            {leads.length === 0 ? <p className="text-slate-500 text-sm">No leads yet.</p> : (
              <ul className="space-y-3">
                {leads.map((l) => (
                  <li key={l.id} className="border border-slate-200 rounded-md p-3">
                    <p className="font-medium">{l.name} <span className="text-slate-400 font-normal text-sm">· {new Date(l.created_at).toLocaleString()}</span></p>
                    <p className="text-sm text-slate-600">{l.email} {l.phone && `· ${l.phone}`}</p>
                    {l.message && <p className="text-sm text-slate-500 mt-1">{l.message}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
